import { Box, Button, Chip, CircularProgress, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Fetch from "../../services/Fetch";
import AuthContext from "../../context/AuthContext";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { APP_NAME } from '../../constants/brand';
import LoginSideImage from '../../images/login/login-side-onboarding.png';
import LogoImage from '../../images/logo/logo.png';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2.5,
        bgcolor: 'background.paper',
    },
};

const resolveDegreeLevel = (degree) => {
    if (!degree) return '';

    const explicitLevel = String(degree.level || '').toLowerCase().trim();
    if (explicitLevel) return explicitLevel;

    const degreeLabel = `${degree.name_en || ''} ${degree.name_ar || ''}`.toLowerCase();

    if (/bachelor|master|phd|diploma|university|بكالوريوس|ماجستير|دكتوراه|دبلوم|جامعي/.test(degreeLabel)) {
        return 'university';
    }

    if (/school|grade|primary|secondary|بكلوريا|ثانوي|ابتدائي|إعدادي|مدرسي/.test(degreeLabel)) {
        return 'school';
    }

    return '';
};

function Onboarding() {
    const host = `${process.env.REACT_APP_LOCAL_HOST}`;
    const { wait, refreshUser, user } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { sendWait, setSendWait } = useWaits();
    const [degrees, setDegrees] = useState([]);
    const [majors, setMajors] = useState([]);
    const [universities, setUniversities] = useState([]);
    const [academicDegreeId, setAcademicDegreeId] = useState('');
    const [majorId, setMajorId] = useState('');
    const [universityId, setUniversityId] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (user?.major?.id) navigate('/home', { replace: true });
    }, [user, navigate]);

    useEffect(() => {
        const load = async () => {
            const [d, m, u] = await Promise.all([
                Fetch(host + '/academic-degrees?per_page=100', 'GET'),
                Fetch(host + '/majors?per_page=200', 'GET'),
                Fetch(host + '/universities?per_page=100', 'GET'),
            ]);
            if (d.status === 200) setDegrees(d.data.data || []);
            if (m.status === 200) setMajors(m.data.data || []);
            if (u.status === 200) setUniversities(u.data.data || []);
        };
        load();
    }, [host]);

    const selectedDegree = degrees.find(d => String(d.id) === String(academicDegreeId));
    const degreeLevel = resolveDegreeLevel(selectedDegree);

    const filteredMajors = majors.filter(m => {
        if (universityId && m.university?.id !== Number(universityId)) return false;
        if (degreeLevel && m.level && m.level !== degreeLevel) return false;
        return true;
    });

    const save = async () => {
        if (!majorId || !academicDegreeId) {
            setSnackBar('error', 'اختر المرحلة الدراسية والتخصص');
            return;
        }
        setSendWait(true);
        const formData = new FormData();
        formData.append('major_id', majorId);
        formData.append('academic_degree_id', academicDegreeId);
        const result = await Fetch(host + '/account/update-profile', 'POST', formData);
        if (result.status === 200) {
            await refreshUser();
            navigate('/home');
        } else {
            setSnackBar('error', result.data?.errors?.[0] || 'تعذر الحفظ');
        }
        setSendWait(false);
    };

    if (wait) {
        return (
            <Box className="w-full h-screen flex justify-center items-center">
                <CircularProgress size={70} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.background.default
                    : 'linear-gradient(180deg, #eef4ff 0%, #f8fbff 100%)',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1fr minmax(520px, 620px)' },
            }}
        >
            <Box
                sx={{
                    display: { xs: 'none', lg: 'block' },
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #0f4cbd 0%, #63b3ed 100%)',
                }}
            >
                <Box
                    component="img"
                    src={LoginSideImage}
                    alt="Student onboarding illustration"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(10, 37, 99, 0.30) 0%, rgba(10, 37, 99, 0.76) 100%)',
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        height: '100%',
                        px: 8,
                        py: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        color: '#ffffff',
                    }}
                >
                    <Chip
                        label="Student Profile"
                        sx={{
                            alignSelf: 'flex-start',
                            mb: 3,
                            bgcolor: 'rgba(255,255,255,0.16)',
                            color: '#ffffff',
                            fontWeight: 700,
                            borderRadius: '999px',
                        }}
                    />
                    <Typography variant="h3" sx={{ fontWeight: 800, maxWidth: 540, lineHeight: 1.25, mb: 2 }}>
                        أكمل بياناتك الأكاديمية للدخول إلى تجربة تعلم مخصصة
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 560, color: 'rgba(255,255,255,0.86)', lineHeight: 1.9 }}>
                        اختر المرحلة الدراسية والجامعة والتخصص حتى نعرض لك المحتوى المناسب داخل المنصة ونجهز ملفك الطلابي بشكل كامل.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3, md: 4 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: 620,
                        borderRadius: { xs: 4, md: '40px 0 0 40px' },
                        border: '1px solid rgba(148, 163, 184, 0.18)',
                        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.10)',
                        overflow: 'hidden',
                        bgcolor: 'rgba(255,255,255,0.96)',
                    }}
                >
                    <Box sx={{ p: { xs: 3, sm: 4, md: 5 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box className="auth-logo-card" sx={{ m: 0 }}>
                                <Box component="img" src={LogoImage} alt={APP_NAME} className="auth-logo" />
                            </Box>
                            <Box>
                                <Chip
                                    label="Welcome"
                                    sx={{
                                        mb: 1,
                                        bgcolor: '#e0ecff',
                                        color: '#0d47a1',
                                        fontWeight: 700,
                                        borderRadius: '999px',
                                    }}
                                />
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    إكمال الملف الأكاديمي
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.9 }}>
                            أهلًا
                            {' '}
                            <Box component="span" sx={{ color: '#0d47a1', fontWeight: 700 }}>
                                {user?.first_name || 'بك'}
                            </Box>
                            ، بقيت خطوة قصيرة لإعداد حسابك قبل متابعة التصفح والدورات.
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 2,
                                p: 2.5,
                                borderRadius: 4,
                                bgcolor: '#f8fbff',
                                border: '1px solid #dbeafe',
                            }}
                        >
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                                    البريد الإلكتروني
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                                    {user?.email || '—'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                                    التخصصات المتاحة
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                                    {filteredMajors.length} تخصص
                                </Typography>
                            </Box>
                        </Box>

                        <TextField
                            select
                            fullWidth
                            label="المرحلة الدراسية"
                            value={academicDegreeId}
                            onChange={(e) => {
                                setAcademicDegreeId(e.target.value);
                                setMajorId('');
                            }}
                            sx={fieldSx}
                        >
                            {degrees.map((d) => (
                                <MenuItem key={d.id} value={d.id}>{d.name_ar || d.name_en}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            label="الجامعة (اختياري)"
                            value={universityId}
                            onChange={(e) => {
                                setUniversityId(e.target.value);
                                setMajorId('');
                            }}
                            sx={fieldSx}
                        >
                            <MenuItem value="">كل الجامعات</MenuItem>
                            {universities.map((u) => (
                                <MenuItem key={u.id} value={u.id}>{u.name_ar || u.name_en}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            label="التخصص"
                            value={majorId}
                            onChange={(e) => setMajorId(e.target.value)}
                            sx={fieldSx}
                            disabled={!academicDegreeId}
                            helperText={!academicDegreeId ? 'اختر المرحلة الدراسية أولًا' : `${filteredMajors.length} تخصص متاح`}
                        >
                            {academicDegreeId && filteredMajors.length === 0 && (
                                <MenuItem value="" disabled>
                                    لا توجد تخصصات مطابقة لهذه المرحلة أو الجامعة
                                </MenuItem>
                            )}
                            {filteredMajors.map((m) => (
                                <MenuItem key={m.id} value={m.id}>{m.name_ar || m.name_en}</MenuItem>
                            ))}
                        </TextField>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={save}
                            disabled={sendWait}
                            sx={{
                                py: 1.8,
                                borderRadius: 3,
                                fontWeight: 700,
                                fontSize: '1rem',
                                boxShadow: 'none',
                                bgcolor: '#0d47a1',
                                '&:hover': { bgcolor: '#0b3b84', boxShadow: 'none' },
                            }}
                        >
                            {sendWait ? <CircularProgress size={22} color="inherit" /> : 'حفظ ومتابعة'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </Box>
    );
}

export default Onboarding;
